import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Truck, Leaf, Shield, ArrowLeft, Handshake } from "lucide-react";
import api, { formatPrice, resolveImg } from "../lib/api";
import SmartImage from "../components/SmartImage";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import usePageMeta from "../hooks/usePageMeta";
import StockBadge from "../components/StockBadge";
import { stockOf } from "../lib/stock";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState("");

  usePageMeta({
    title: product ? `${product.name} — $${product.price} | DC Gift World Too` : "DC Gift World Too",
    description: product
      ? `${(product.description || "").replace(/\s+/g, " ").slice(0, 155)}...`
      : "Shop pure essential oils, DIY supplies, and garden gifts at DC Gift World Too.",
  });

  // JSON-LD structured data for Google product rich results
  useEffect(() => {
    if (!product) return;
    const jsonLd = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "description": (product.description || "").replace(/\s+/g, " ").slice(0, 500),
      "image": [resolveImg(product.image_url)],
      "sku": product.id,
      "brand": { "@type": "Brand", "name": "DC Gift World Too" },
      "offers": {
        "@type": "Offer",
        "url": typeof window !== "undefined" ? window.location.href : "",
        "priceCurrency": "USD",
        "price": product.price.toFixed(2),
        "availability": (product.stock ?? 1) > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition",
      },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "product-jsonld";
    el.text = JSON.stringify(jsonLd);
    // Remove any prior tag before appending
    const prior = document.getElementById("product-jsonld");
    if (prior) prior.remove();
    document.head.appendChild(el);
    return () => {
      const tag = document.getElementById("product-jsonld");
      if (tag) tag.remove();
    };
  }, [product]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        setActiveImage(res.data.image_url);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleAdd = () => {
    if (!product) return;
    const added = addItem(product, qty);
    if (added) toast.success(`Added ${added} × ${product.name}`);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-[var(--muted)] rounded-2xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-[var(--muted)] rounded animate-pulse" />
            <div className="h-4 bg-[var(--muted)] rounded animate-pulse w-1/2" />
            <div className="h-32 bg-[var(--muted)] rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h2 className="font-serif text-4xl text-[var(--ink-forest)]">Product not found</h2>
        <Link
          to="/shop"
          className="mt-6 inline-block bg-[var(--terracotta)] text-white rounded-full px-6 py-3 text-sm"
        >
          Back to shop
        </Link>
      </div>
    );
  }

  const stock = stockOf(product);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-16" data-testid="product-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--muted-fg)] hover:text-[var(--terracotta)] mb-8"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Image gallery */}
        <div className="relative">
          <div className="aspect-square overflow-hidden rounded-3xl bg-[var(--muted)] border border-[var(--border)]">
            <SmartImage
              src={resolveImg(activeImage || product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover transition-opacity duration-300"
              data-testid="product-main-image"
            />
          </div>
          <div className="absolute top-5 left-5 bg-[var(--bg-cream)]/90 backdrop-blur-md rounded-full px-3 py-1 text-xs font-semibold text-[var(--terracotta)] border border-[var(--border)]">
            {categoryLabel(product.category)}
          </div>

          {/* Thumbnails (only if we have more than one image) */}
          {(product.images && product.images.length > 0) && (
            <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-thumbnails">
              {[product.image_url, ...product.images].map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(img)}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    activeImage === img
                      ? "border-[var(--terracotta)] shadow-md"
                      : "border-[var(--border)] opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`View image ${i + 1}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:pt-4">
          <p className="eyebrow mb-3">{categoryLabel(product.category)}</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
            {product.name}
          </h1>
          <p className="mt-4 font-serif text-3xl text-[var(--terracotta)]" data-testid="product-price">
            {formatPrice(product.price)}
          </p>
          <StockBadge stock={stock} className="mt-3" testid="product-stock-badge" />

          <p className="mt-6 text-[var(--muted-fg)] leading-relaxed text-base whitespace-pre-line">{product.description}</p>

          {/* Simply Earth Ambassador badge — only on essential oils */}
          {product.category === "essential-oils" && (
            <Link
              to="/about#about-simply-earth"
              className="mt-6 flex items-start gap-3 bg-[var(--bg-cream)] border border-[var(--terracotta)]/25 rounded-2xl p-4 hover:border-[var(--terracotta)]/60 transition-colors group"
              data-testid="simply-earth-badge"
            >
              <div className="w-9 h-9 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center shrink-0">
                <Handshake className="w-4 h-4 text-[var(--terracotta)]" />
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[var(--terracotta)]">
                  Simply Earth Ambassador
                </p>
                <p className="mt-1 text-sm text-[var(--ink-forest)] leading-snug">
                  Sourced through Simply Earth — 13% of their profits go to ending human trafficking.
                  <span className="ml-1 underline underline-offset-2 group-hover:text-[var(--terracotta)]">
                    Read our story →
                  </span>
                </p>
              </div>
            </Link>
          )}

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <span
                  key={t}
                  className="text-xs px-3 py-1 rounded-full bg-[var(--muted)] text-[var(--ink-forest)]"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Quantity */}
          {stock > 0 && (
          <div className="mt-10 flex items-center gap-4">
            <span className="text-sm font-medium text-[var(--ink-forest)]">Quantity</span>
            <div className="inline-flex items-center border border-[var(--border)] rounded-full bg-[var(--bg-surface)]">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-3 hover:bg-[var(--muted)] rounded-full"
                data-testid="qty-minus-btn"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="px-5 font-medium" data-testid="qty-display">{qty}</span>
              <button
                onClick={() => setQty((q) => Math.min(stock, q + 1))}
                disabled={qty >= stock}
                className="p-3 hover:bg-[var(--muted)] rounded-full disabled:opacity-30 disabled:hover:bg-transparent"
                data-testid="qty-plus-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {qty >= stock && stock <= 5 && (
              <span className="text-xs text-[var(--muted-fg)]" data-testid="qty-max-note">That's all we have</span>
            )}
          </div>
          )}

          <button
            onClick={handleAdd}
            disabled={stock <= 0}
            className="mt-6 w-full bg-[var(--terracotta)] text-white rounded-full py-4 text-base font-semibold hover:bg-[var(--terracotta-deep)] transition-colors disabled:bg-[var(--muted)] disabled:text-[var(--muted-fg)] disabled:cursor-not-allowed"
            data-testid="add-to-cart-btn"
          >
            {stock <= 0 ? "Out of stock" : `Add to basket — ${formatPrice(product.price * qty)}`}
          </button>

          {/* Trust badges */}
          <div className="mt-10 grid grid-cols-3 gap-4">
            <Trust icon={Truck} title="Free shipping" body="Over $75" />
            <Trust icon={Leaf} title="Hand-curated" body="With care" />
            <Trust icon={Shield} title="Secure" body="Stripe checkout" />
          </div>

          {/* Details */}
          <div className="mt-10 border-t border-[var(--border)] pt-6 space-y-3">
            <Detail title="Shipping">
              Orders ship in 1–3 business days from our little studio in Limon, CO. If an item needs to come from our Simply Earth partner, please allow a few extra days — we&apos;ll always keep you posted. Free shipping on orders over $75; flat $7.99 below.
            </Detail>
            <Detail title="Returns">
              30-day returns on unopened items. Contact us at hello@dcgiftworldtoo.com.
            </Detail>
          </div>
        </div>
      </div>
    </div>
  );
}

function Trust({ icon: Icon, title, body }) {
  return (
    <div className="text-center p-4 bg-[var(--muted)]/50 rounded-2xl">
      <Icon className="w-5 h-5 text-[var(--terracotta)] mx-auto mb-1.5" />
      <p className="text-xs font-semibold text-[var(--ink-forest)]">{title}</p>
      <p className="text-xs text-[var(--muted-fg)]">{body}</p>
    </div>
  );
}

function Detail({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--border)] pb-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left py-2"
      >
        <span className="font-medium text-[var(--ink-forest)]">{title}</span>
        <Plus className={`w-4 h-4 transition-transform ${open ? "rotate-45" : ""}`} />
      </button>
      {open && <p className="text-sm text-[var(--muted-fg)] pb-2">{children}</p>}
    </div>
  );
}

function categoryLabel(c) {
  if (c === "essential-oils") return "Essential Oil";
  if (c === "diy-supplies") return "DIY Supply";
  if (c === "garden-gifts") return "Home, Garden & Gift";
  return c;
}
